<template>
    <div>
        <BackHeader
            title="Preguntas frecuentes"
            :onBack="() => $router.push('/configuracion')"
        ></BackHeader>

        <p class="mb-6">
            Estas son las secciones y las preguntas que ve el alumno en la página
            de preguntas frecuentes. Arrastra una sección o una pregunta por su
            manija para cambiar el orden. Para llevar una pregunta a otra sección,
            elígela en el formulario de la pregunta.
        </p>

        <v-row>
            <!-- Secciones con sus preguntas -->
            <v-col cols="12" md="4">
                <v-card class="pa-3">
                    <!-- La bienvenida va siempre arriba y sin manija: es la
                         pregunta "index", que no cuelga de ninguna sección y
                         el backend no deja mover, renombrar ni borrar. Se
                         muestra aunque todavia no exista la fila; se crea al
                         guardarla por primera vez. -->
                    <div
                        class="fila"
                        :class="{ activa: esActiva('bienvenida', bienvenida.id) }"
                        @click="seleccionar('bienvenida', bienvenida)"
                    >
                        <v-icon small class="mr-2">mdi-lock-outline</v-icon>
                        <div class="texto">
                            <div class="font-weight-medium">Bienvenida</div>
                            <div class="caption">
                                {{ bienvenida.id ? "Siempre va primero" : "Sin contenido todavía" }}
                            </div>
                        </div>
                    </div>

                    <v-divider class="my-3"></v-divider>

                    <v-btn
                        block
                        elevation="0"
                        color="primary"
                        class="mb-3"
                        @click="siNoHayCambios(crearSeccion)"
                    >
                        <v-icon left small>mdi-plus</v-icon>
                        Nueva sección
                    </v-btn>

                    <!-- Dos niveles de arrastre con manijas distintas: la de la
                         sección no agarra las preguntas y al reves. Las
                         preguntas no pasan de una lista a otra, porque el
                         backend reordena de a una seccion por vez. -->
                    <draggable
                        v-model="secciones"
                        handle=".manija-seccion"
                        @start="recordarOrden(secciones)"
                        @end="guardarOrdenSecciones"
                    >
                        <div
                            v-for="seccion in secciones"
                            :key="seccion.id"
                            class="bloque"
                        >
                            <div
                                class="fila"
                                :class="{ activa: esActiva('seccion', seccion.id) }"
                                @click="seleccionar('seccion', seccion)"
                            >
                                <v-icon small class="manija manija-seccion mr-1">mdi-drag</v-icon>
                                <v-btn
                                    icon
                                    x-small
                                    class="mr-1"
                                    :title="plegadas[seccion.id] ? 'Mostrar preguntas' : 'Ocultar preguntas'"
                                    @click.stop="plegar(seccion)"
                                >
                                    <v-icon small>
                                        {{ plegadas[seccion.id] ? "mdi-chevron-right" : "mdi-chevron-down" }}
                                    </v-icon>
                                </v-btn>
                                <div class="texto font-weight-medium">
                                    {{ seccion.nombre || "(sin nombre)" }}
                                </div>
                                <span class="caption ml-2">{{ seccion.preguntas.length }}</span>
                            </div>

                            <div v-show="!plegadas[seccion.id]" class="preguntas">
                                <draggable
                                    v-model="seccion.preguntas"
                                    handle=".manija-pregunta"
                                    @start="recordarOrden(seccion.preguntas)"
                                    @end="guardarOrdenPreguntas(seccion)"
                                >
                                    <div
                                        v-for="pregunta in seccion.preguntas"
                                        :key="pregunta.id"
                                        class="fila"
                                        :class="{ activa: esActiva('pregunta', pregunta.id) }"
                                        @click="seleccionar('pregunta', pregunta)"
                                    >
                                        <v-icon small class="manija manija-pregunta mr-2">mdi-drag</v-icon>
                                        <div class="texto">
                                            {{ pregunta.pregunta || "(sin texto)" }}
                                        </div>
                                    </div>
                                </draggable>

                                <v-btn
                                    text
                                    small
                                    color="primary"
                                    class="mt-1"
                                    @click="siNoHayCambios(() => crearPregunta(seccion))"
                                >
                                    <v-icon left small>mdi-plus</v-icon>
                                    Nueva pregunta
                                </v-btn>
                            </div>
                        </div>
                    </draggable>

                    <p
                        v-if="!secciones.length && !cargando"
                        class="caption text-center my-3"
                    >
                        Todavía no hay secciones.
                    </p>
                </v-card>
            </v-col>

            <!-- Edición de lo elegido -->
            <v-col cols="12" md="8" class="panel-edicion">
                <v-card v-if="editandoSeccion" class="pa-4">
                    <v-text-field
                        v-model="formulario.nombre"
                        label="Nombre de la sección"
                        rounded
                        filled
                        :error-messages="errores.nombre"
                    ></v-text-field>

                    <v-card-actions class="px-0">
                        <v-btn
                            elevation="0"
                            color="primary"
                            :disabled="!hayCambios || guardando"
                            @click="guardar"
                        >
                            Guardar cambios
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn text color="error" @click="pedirBorrado">
                            Eliminar sección
                        </v-btn>
                    </v-card-actions>
                </v-card>

                <v-card v-else-if="editandoBienvenida" class="pa-4">
                    <v-text-field
                        value="Bienvenida"
                        label="Pregunta"
                        rounded
                        filled
                        disabled
                        hint="El nombre de la bienvenida no se puede cambiar."
                        persistent-hint
                    ></v-text-field>

                    <Editor v-model="formulario.respuesta" class="mb-4" />

                    <p v-if="errores.respuesta.length" class="error--text caption">
                        {{ errores.respuesta.join(" ") }}
                    </p>

                    <v-card-actions class="px-0">
                        <v-btn
                            elevation="0"
                            color="primary"
                            :disabled="!hayCambios || guardando"
                            @click="guardar"
                        >
                            Guardar cambios
                        </v-btn>
                    </v-card-actions>
                </v-card>

                <v-card v-else-if="formulario" class="pa-4">
                    <v-textarea
                        v-model="formulario.pregunta"
                        label="Pregunta"
                        filled
                        auto-grow
                        rows="1"
                        :error-messages="errores.pregunta"
                    ></v-textarea>

                    <v-select
                        v-model="formulario.seccion"
                        :items="secciones"
                        item-text="nombre"
                        item-value="id"
                        label="Sección"
                        filled
                        :error-messages="errores.seccion"
                    ></v-select>

                    <Editor v-model="formulario.respuesta" class="mb-4" />

                    <p v-if="errores.respuesta.length" class="error--text caption">
                        {{ errores.respuesta.join(" ") }}
                    </p>

                    <v-card-actions class="px-0">
                        <v-btn
                            elevation="0"
                            color="primary"
                            :disabled="!hayCambios || guardando"
                            @click="guardar"
                        >
                            Guardar cambios
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn text color="error" @click="pedirBorrado">
                            Eliminar pregunta
                        </v-btn>
                    </v-card-actions>
                </v-card>

                <v-card v-else class="pa-8 text-center">
                    <p class="mb-0">
                        Elige una sección o una pregunta de la lista para editarla.
                    </p>
                </v-card>
            </v-col>
        </v-row>

        <ActionModal
            :active="confirmarBorrado"
            :title="avisoBorrado.titulo"
            :successAction="borrar"
            :cancelAction="() => (confirmarBorrado = false)"
        >
            {{ avisoBorrado.texto }}
        </ActionModal>

        <ActionModal
            :active="confirmarDescarte"
            title="Cambios sin guardar"
            :successAction="descartarYSeguir"
            :cancelAction="() => ((confirmarDescarte = false), (pendiente = null))"
        >
            Tienes cambios sin guardar en «{{ nombreDescarte }}».
            Si continúas se pierden.
        </ActionModal>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import BackHeader from "@/components/content/BackHeader";
import ActionModal from "@/components/ActionModal";
import Editor from "@/components/forms/Editor";
import { tablesService } from "@/services";

// Los campos que el backend puede rechazar y que se muestran junto a su control.
function sinErrores() {
    return { nombre: [], pregunta: [], seccion: [], respuesta: [] };
}

// Siguiente `orden` libre de una lista: uno más que el mayor, para que lo nuevo
// quede al final aunque haya huecos.
function siguienteOrden(lista) {
    return lista.length ? Math.max(...lista.map((item) => item.orden)) + 1 : 1;
}

export default {
    components: { draggable, BackHeader, ActionModal, Editor },

    data: () => ({
        // Tal como las devuelve /api/seccionpregunta/: cada seccion trae su
        // arreglo `preguntas`, ya ordenado.
        secciones: [],
        // La pregunta "index" de /api/pregunta/bienvenida/. Sin `id` mientras
        // no exista la fila: el primer guardado es el que la crea.
        bienvenida: { id: null, respuesta: "" },
        // id de seccion -> true si sus preguntas estan ocultas en la lista.
        plegadas: {},
        // { tipo: "bienvenida" | "seccion" | "pregunta", id, ...campos editables }
        formulario: null,
        original: null,
        ordenPrevio: [],
        cargando: true,
        guardando: false,
        confirmarBorrado: false,
        confirmarDescarte: false,
        // Lo que se hace si el administrador acepta descartar sus cambios.
        pendiente: null,
        // Los textos de los dos avisos se fijan al abrirlos: si se calcularan
        // del formulario, cambiarian mientras el modal se desvanece, que es
        // justo cuando el formulario pasa a otra cosa.
        avisoBorrado: { titulo: "", texto: "" },
        nombreDescarte: "",
        errores: sinErrores(),
    }),

    computed: {
        editandoSeccion() {
            return !!this.formulario && this.formulario.tipo === "seccion";
        },

        editandoBienvenida() {
            return !!this.formulario && this.formulario.tipo === "bienvenida";
        },

        hayCambios() {
            if (!this.formulario || !this.original) {
                return false;
            }
            return Object.keys(this.formulario).some(
                (campo) => this.formulario[campo] !== this.original[campo]
            );
        },

        // Los avisos nombran lo que está guardado, no lo que se está escribiendo.
        nombreGuardado() {
            if (!this.original) {
                return "";
            }
            if (this.original.tipo === "bienvenida") {
                return "Bienvenida";
            }
            return this.original.tipo === "seccion"
                ? this.original.nombre
                : this.original.pregunta;
        },

    },

    created() {
        this.cargar();
    },

    methods: {
        cargar() {
            this.cargando = true;
            Promise.all([
                tablesService.getService("seccionpregunta"),
                tablesService.getService("pregunta/bienvenida"),
            ])
                .then(([secciones, bienvenida]) => {
                    this.secciones = secciones;
                    this.bienvenida = bienvenida;
                    this.cargando = false;
                    this.abrir("bienvenida", bienvenida);
                })
                .catch(() => {
                    this.cargando = false;
                    this.$store.dispatch(
                        "alert/error",
                        "No se pudieron cargar las preguntas frecuentes."
                    );
                });
        },

        buscarSeccion(id) {
            return this.secciones.find((seccion) => seccion.id === id);
        },

        seccionDePregunta(id) {
            return this.secciones.find((seccion) =>
                seccion.preguntas.some((pregunta) => pregunta.id === id)
            );
        },

        esActiva(tipo, id) {
            return (
                !!this.formulario &&
                this.formulario.tipo === tipo &&
                this.formulario.id === id
            );
        },

        plegar(seccion) {
            this.$set(this.plegadas, seccion.id, !this.plegadas[seccion.id]);
        },

        seleccionar(tipo, item) {
            if (this.esActiva(tipo, item.id)) {
                return;
            }
            this.siNoHayCambios(() => this.abrir(tipo, item));
        },

        // Todo lo que reemplaza el formulario pasa por aquí: con cambios sin
        // guardar, primero se pide confirmación.
        siNoHayCambios(accion) {
            if (this.hayCambios) {
                this.pendiente = accion;
                this.nombreDescarte = this.nombreGuardado;
                this.confirmarDescarte = true;
                return;
            }
            accion();
        },

        descartarYSeguir() {
            this.confirmarDescarte = false;
            const accion = this.pendiente;
            this.pendiente = null;
            if (accion) {
                accion();
            }
        },

        abrir(tipo, item) {
            this.errores = sinErrores();
            this.formulario =
                tipo === "bienvenida"
                    ? { tipo, id: item.id, respuesta: item.respuesta }
                    : tipo === "seccion"
                    ? { tipo, id: item.id, nombre: item.nombre }
                    : {
                          tipo,
                          id: item.id,
                          pregunta: item.pregunta,
                          respuesta: item.respuesta,
                          seccion: item.seccion,
                      };
            // Copia aparte para saber si hay cambios sin guardar.
            this.original = { ...this.formulario };
        },

        guardar() {
            this.guardando = true;
            this.errores = sinErrores();

            const guardados = {
                bienvenida: [this.guardarBienvenida, "Bienvenida guardada."],
                seccion: [this.guardarSeccion, "Sección guardada."],
                pregunta: [this.guardarPregunta, "Pregunta guardada."],
            };
            const [peticion, aviso] = guardados[this.formulario.tipo];

            peticion()
                .then(() => {
                    this.guardando = false;
                    this.$store.dispatch("alert/success", aviso);
                })
                .catch((error) => {
                    this.guardando = false;
                    this.mostrarErrores(error);
                });
        },

        // La bienvenida va por su propia acción, que crea la fila si falta y
        // la actualiza si ya está. Su texto y su orden los pone el servidor,
        // así que solo viaja la respuesta.
        guardarBienvenida() {
            return tablesService
                .newService("pregunta/bienvenida", {
                    respuesta: this.formulario.respuesta,
                })
                .then((bienvenida) => {
                    this.bienvenida = bienvenida;
                    this.abrir("bienvenida", bienvenida);
                });
        },

        guardarSeccion() {
            return tablesService
                .editService("seccionpregunta", `${this.formulario.id}/`, {
                    nombre: this.formulario.nombre,
                })
                .then((seccion) => {
                    const local = this.buscarSeccion(seccion.id);
                    if (local) {
                        local.nombre = seccion.nombre;
                    }
                    this.abrir("seccion", seccion);
                });
        },

        guardarPregunta() {
            const datos = {
                pregunta: this.formulario.pregunta,
                respuesta: this.formulario.respuesta,
            };
            // Al cambiar de seccion la pregunta entra al final de la nueva.
            // El orden de la seccion que deja no se toca: quedan huecos, pero
            // solo importa el orden relativo.
            if (this.formulario.seccion !== this.original.seccion) {
                const destino = this.buscarSeccion(this.formulario.seccion);
                datos.seccion = this.formulario.seccion;
                datos.orden = siguienteOrden(destino ? destino.preguntas : []);
            }

            return tablesService
                .editService("pregunta", `${this.formulario.id}/`, datos)
                .then((pregunta) => {
                    // La respuesta vuelve saneada por el backend: se adopta la
                    // suya para que lo que se ve sea lo que quedó guardado.
                    this.ubicarPregunta(pregunta);
                    this.abrir("pregunta", pregunta);
                });
        },

        // Deja la pregunta guardada en la lista de su sección: en su mismo
        // lugar si no cambió de sección, al final de la nueva si cambió.
        ubicarPregunta(pregunta) {
            const actual = this.seccionDePregunta(pregunta.id);
            if (actual && actual.id === pregunta.seccion) {
                const posicion = actual.preguntas.findIndex(
                    (item) => item.id === pregunta.id
                );
                this.$set(actual.preguntas, posicion, pregunta);
                return;
            }
            if (actual) {
                actual.preguntas = actual.preguntas.filter(
                    (item) => item.id !== pregunta.id
                );
            }
            const destino = this.buscarSeccion(pregunta.seccion);
            if (destino) {
                destino.preguntas.push(pregunta);
                this.$set(this.plegadas, destino.id, false);
            }
        },

        crearSeccion() {
            tablesService
                .newService("seccionpregunta", {
                    nombre: "Nueva sección",
                    orden: siguienteOrden(this.secciones),
                })
                .then((seccion) => {
                    this.secciones.push(seccion);
                    this.abrir("seccion", seccion);
                })
                .catch(() => {
                    this.$store.dispatch("alert/error", "No se pudo crear la sección.");
                });
        },

        crearPregunta(seccion) {
            tablesService
                .newService("pregunta", {
                    pregunta: "Nueva pregunta",
                    respuesta: "",
                    seccion: seccion.id,
                    orden: siguienteOrden(seccion.preguntas),
                })
                .then((pregunta) => {
                    seccion.preguntas.push(pregunta);
                    this.$set(this.plegadas, seccion.id, false);
                    this.abrir("pregunta", pregunta);
                })
                .catch(() => {
                    this.$store.dispatch("alert/error", "No se pudo crear la pregunta.");
                });
        },

        pedirBorrado() {
            const final =
                "Dejará de verse en la página de preguntas frecuentes y no se puede deshacer.";

            if (!this.editandoSeccion) {
                this.avisoBorrado = {
                    titulo: "Eliminar pregunta",
                    texto: `¿Eliminar «${this.nombreGuardado}»? ${final}`,
                };
            } else {
                // El borrado va en cascada en el backend: se lleva las preguntas.
                const seccion = this.buscarSeccion(this.formulario.id);
                const cantidad = seccion ? seccion.preguntas.length : 0;
                const arrastra =
                    cantidad === 0
                        ? ""
                        : cantidad === 1
                        ? " Se eliminará también su pregunta."
                        : ` Se eliminarán también sus ${cantidad} preguntas.`;
                this.avisoBorrado = {
                    titulo: "Eliminar sección",
                    texto: `¿Eliminar «${this.nombreGuardado}»?${arrastra} ${final}`,
                };
            }
            this.confirmarBorrado = true;
        },

        borrar() {
            const { tipo, id } = this.formulario;
            this.confirmarBorrado = false;

            tablesService
                .deleteService(tipo === "seccion" ? "seccionpregunta" : "pregunta", `${id}/`)
                .then(() => {
                    if (tipo === "seccion") {
                        this.secciones = this.secciones.filter((seccion) => seccion.id !== id);
                    } else {
                        const seccion = this.seccionDePregunta(id);
                        if (seccion) {
                            seccion.preguntas = seccion.preguntas.filter(
                                (pregunta) => pregunta.id !== id
                            );
                        }
                    }
                    this.formulario = null;
                    this.original = null;
                    this.$store.dispatch(
                        "alert/success",
                        tipo === "seccion" ? "Sección eliminada." : "Pregunta eliminada."
                    );
                })
                .catch(() => {
                    this.$store.dispatch(
                        "alert/error",
                        tipo === "seccion"
                            ? "No se pudo eliminar la sección."
                            : "No se pudo eliminar la pregunta."
                    );
                });
        },

        recordarOrden(lista) {
            this.ordenPrevio = lista.slice();
        },

        mismoOrden(lista) {
            return (
                lista.map((item) => item.id).join(",") ===
                this.ordenPrevio.map((item) => item.id).join(",")
            );
        },

        // En los dos reordenar se manda la lista completa: el backend la exige
        // para que un orden calculado sobre una pantalla desactualizada falle
        // en vez de dejar huecos. Si falla, la lista vuelve a como estaba:
        // dejarla en el orden nuevo haría creer que se guardó.
        guardarOrdenSecciones() {
            if (this.mismoOrden(this.secciones)) {
                return;
            }
            const previo = this.ordenPrevio;
            tablesService
                .newService("seccionpregunta/reordenar", {
                    orden: this.secciones.map((seccion) => seccion.id),
                })
                .then((secciones) => {
                    this.secciones = secciones;
                })
                .catch(() => {
                    this.secciones = previo;
                    this.$store.dispatch("alert/error", "No se pudo guardar el orden.");
                });
        },

        guardarOrdenPreguntas(seccion) {
            if (this.mismoOrden(seccion.preguntas)) {
                return;
            }
            const previo = this.ordenPrevio;
            tablesService
                .newService("pregunta/reordenar", {
                    seccion: seccion.id,
                    orden: seccion.preguntas.map((pregunta) => pregunta.id),
                })
                .then((preguntas) => {
                    seccion.preguntas = preguntas;
                })
                .catch(() => {
                    seccion.preguntas = previo;
                    this.$store.dispatch("alert/error", "No se pudo guardar el orden.");
                });
        },

        mostrarErrores(error) {
            const datos = error && error.response && error.response.data;
            const errores = sinErrores();
            if (datos) {
                Object.keys(errores).forEach((campo) => {
                    errores[campo] = this.comoLista(datos[campo]);
                });
            }
            this.errores = errores;
            if (!Object.keys(errores).some((campo) => errores[campo].length)) {
                this.$store.dispatch("alert/error", "No se pudo guardar.");
            }
        },

        comoLista(valor) {
            if (!valor) {
                return [];
            }
            return Array.isArray(valor) ? valor : [valor];
        },
    },
};
</script>

<style lang="scss" scoped>
.bloque + .bloque {
    margin-top: 6px;
}

.fila {
    display: flex;
    align-items: center;
    padding: 8px;
    border-radius: 6px;
    cursor: pointer;
    border: 1px solid transparent;

    &:hover {
        background-color: rgba(128, 128, 128, 0.12);
    }

    &.activa {
        border-color: var(--v-primary-base);
    }

    .texto {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

// Las preguntas cuelgan de su sección con una sangría y una guía a la
// izquierda, para que se lea a qué sección pertenece cada una.
.preguntas {
    margin-left: 18px;
    padding-left: 8px;
    border-left: 2px solid rgba(128, 128, 128, 0.2);
}

.manija {
    cursor: grab;
}

// Con la lista larga, el panel de edición acompaña el scroll para que la
// pregunta elegida siga a la vista aunque esté al final de la lista.
@media (min-width: 960px) {
    .panel-edicion {
        position: sticky;
        top: 12px;
        align-self: flex-start;
    }
}
</style>
